import React from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import {
    Apple as AppleIcon,
    Android as AndroidIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import AdSense from "../../components/AdSense";
import SEO from "../../components/SEO/SEO";
import PageHero from "../../components/PageHero/PageHero";
import AppDownloadCTA, {
    PageShell,
} from "../../components/AppDownloadCTA/AppDownloadCTA";
import ScreenshotGallery, {
    PhoneMockup,
} from "../../components/ScreenshotGallery/ScreenshotGallery";
import { pageMeta } from "../../seo/pageMeta";
import { ANDROID_APP_URL, IOS_APP_URL, SITE_URL } from "../../seo/constants";
import { HERO_SCREENSHOT } from "../../seo/assets";

const features = [
    {
        title: "AI Quran Chat & Tafsir",
        body: "Ask Islamic and Quran questions anytime. Get clear explanations, translations, and beginner-friendly guidance rooted in Quranic learning.",
    },
    {
        title: "Salah Prayer Times & Reminders",
        body: "Accurate location-based Namaz times for Fajr, Dhuhr, Asr, Maghrib, and Isha — with smart reminders so you never miss Salah.",
    },
    {
        title: "Daily Ayat & Reflections",
        body: "Receive inspiring Holy Quran verses every day with meaning and practical takeaways for Muslim life.",
    },
    {
        title: "Islamic Learning & Deen Growth",
        body: "Build habits around Quran reading, dua, and spiritual growth with a peaceful, distraction-free experience.",
    },
];

const faqs = [
    {
        q: "Is Quran Chat Buddy free?",
        a: "Yes. Quran Chat Buddy offers free access to core Islamic features, with optional in-app purchases for premium plans.",
    },
    {
        q: "Can I read the Quran inside the app?",
        a: "Yes. The app includes Quran reading features along with translations and learning tools to help you understand ayat.",
    },
    {
        q: "Does the app provide prayer times and Qibla?",
        a: "Yes. Quran Chat Buddy provides accurate daily prayer times based on your location, plus Islamic companion tools for daily practice.",
    },
    {
        q: "Can I ask Islamic questions with AI?",
        a: "Yes. The AI assistant helps answer many Quran and Islam questions and guides users toward clearer Quranic understanding.",
    },
    {
        q: "Is Quran Chat Buddy available on iPhone and Android?",
        a: "Yes. Quran Chat Buddy is available on the App Store for iPhone and on Google Play for Android.",
    },
];

export default function DeenBuddy() {
    const meta = pageMeta.deenBuddy;

    return (
        <PageShell maxWidth="lg">
            <SEO
                {...meta}
                image={`${SITE_URL}/images/screenshots/screen-1.webp`}
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        name: meta.title,
                        description: meta.description,
                        url: `${SITE_URL}${meta.path}`,
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ]}
            />

            <Grid container spacing={4} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={7}>
                    <PageHero
                        eyebrow="Quran Chat Buddy App"
                        title="Your AI Islamic Companion for Quran, Salah & Deen"
                        subtitle="Quran Chat Buddy helps Muslims strengthen their connection with Allah through AI Quran chat, accurate prayer times, daily ayat, tafsir-style learning, and peaceful spiritual guidance — anytime, anywhere."
                        breadcrumbs={[
                            { label: "Home", to: "/" },
                            { label: "App" },
                        ]}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            justifyContent: { xs: "center", md: "flex-start" },
                            mb: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AppleIcon />}
                            href={IOS_APP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            App Store
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<AndroidIcon />}
                            href={ANDROID_APP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                        >
                            Google Play
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12} md={5}>
                    <PhoneMockup
                        src={HERO_SCREENSHOT.src}
                        alt={HERO_SCREENSHOT.alt}
                        maxWidth={240}
                    />
                </Grid>
            </Grid>

            <Box sx={{ my: 4 }}>
                <AdSense slot="2566597353" />
            </Box>

            <Typography
                variant="h3"
                component="h2"
                sx={{ mb: 3, fontWeight: 700, textAlign: "center" }}
            >
                Why Muslims Choose Quran Chat Buddy
            </Typography>
            <Grid container spacing={3} sx={{ mb: 5 }}>
                {features.map((f) => (
                    <Grid item xs={12} sm={6} key={f.title}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    component="h3"
                                    sx={{ mb: 1, fontWeight: 600, color: "primary.dark" }}
                                >
                                    {f.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary", lineHeight: 1.7 }}
                                >
                                    {f.body}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <ScreenshotGallery
                title="App screenshots from the App Store"
                subtitle="See AI Quran chat, prayer times, daily ayat, and Islamic learning — the same experience available on the App Store listing for Quran Chat Buddy."
                limit={7}
            />

            <Box sx={{ my: 4 }}>
                <AdSense slot="2566597353" />
            </Box>

            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
                Grow Your Deen With Practical Tools
            </Typography>
            <List>
                {[
                    "24/7 AI Islamic assistance for Quran questions",
                    "Accurate Salah prayer times for Muslims worldwide",
                    "Daily ayat reminders and spiritual reflections",
                    "Clean, peaceful interface for students, youth & families",
                    "Free Quran PDF and Surah Yaseen resources on our website",
                ].map((item) => (
                    <ListItem key={item} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                    </ListItem>
                ))}
            </List>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                Explore more:{" "}
                <Box component={Link} to="/quran-pdf-download" sx={{ color: "primary.main" }}>
                    Quran PDF download
                </Box>
                {" · "}
                <Box component={Link} to="/surah-yaseen" sx={{ color: "primary.main" }}>
                    Surah Yaseen PDF
                </Box>
                {" · "}
                <Box component={Link} to="/blogs" sx={{ color: "primary.main" }}>
                    Islamic blogs
                </Box>
            </Typography>

            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
                Frequently Asked Questions
            </Typography>
            {faqs.map((f) => (
                <Accordion
                    key={f.q}
                    disableGutters
                    elevation={0}
                    sx={{
                        mb: 1,
                        border: "1px solid rgba(46,139,87,0.12)",
                        borderRadius: "12px !important",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{f.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            {f.a}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ))}

            <AppDownloadCTA
                title="Start your Islamic journey today"
                subtitle="Join Muslims using Quran Chat Buddy to read the Quran, learn Islam, track prayer times, discover daily wisdom, and grow spiritually every day."
            />
        </PageShell>
    );
}
